import type { TextChunk } from './text-chunker';
import type {
  CheckRulesResponse,
  StatementDisplay,
  ConflictDisplay,
  SimilarityDisplay,
  NearMissDisplay,
  CoverageGapDisplay,
  ExtractChunkResponse,
} from './types';

function shiftStatement(
  s: StatementDisplay,
  base: number,
  prefix: string
): StatementDisplay {
  return {
    ...s,
    iri: `${prefix}${s.iri}`,
    span_begin: s.span_begin != null ? s.span_begin + base : s.span_begin,
    span_end: s.span_end != null ? s.span_end + base : s.span_end,
  };
}

export function mergeCheckRulesResults(
  results: (CheckRulesResponse | null)[],
  chunks: TextChunk[],
  failedIndices: number[] = []
): CheckRulesResponse {
  const statements: StatementDisplay[] = [];
  const conflicts: ConflictDisplay[] = [];
  const similarities: SimilarityDisplay[] = [];
  const nearMisses: NearMissDisplay[] = [];
  const gaps: CoverageGapDisplay[] = [];
  const errors: string[] = [];
  let sentenceCount = 0;
  let statementCount = 0;

  results.forEach((result, i) => {
    if (!result) return;
    const base = chunks[i]!.baseOffset;
    const prefix = `c${i}:`;

    for (const s of result.statements || []) {
      statements.push(shiftStatement(s, base, prefix));
    }
    for (const c of result.conflicts || []) {
      conflicts.push({
        ...c,
        rule_a: `${prefix}${c.rule_a}`,
        rule_b: `${prefix}${c.rule_b}`,
        prevailing_norm: c.prevailing_norm
          ? `${prefix}${c.prevailing_norm}`
          : c.prevailing_norm,
      });
    }
    for (const sim of result.similarities || []) {
      similarities.push({
        ...sim,
        rule_a: `${prefix}${sim.rule_a}`,
        rule_b: `${prefix}${sim.rule_b}`,
      });
    }
    for (const nm of result.near_misses || []) {
      nearMisses.push({
        ...nm,
        span_begin:
          nm.span_begin != null ? nm.span_begin + base : nm.span_begin,
        span_end: nm.span_end != null ? nm.span_end + base : nm.span_end,
      });
    }
    for (const g of result.gaps || []) {
      gaps.push({ ...g, rule: `${prefix}${g.rule}` });
    }
    sentenceCount += result.sentence_count || 0;
    statementCount += result.statement_count || 0;
    if (result.check_error) {
      errors.push(result.check_error);
    }
  });

  return {
    statements,
    conflicts: conflicts.length ? conflicts : null,
    similarities: similarities.length ? similarities : null,
    near_misses: nearMisses.length ? nearMisses : null,
    gaps: gaps.length ? gaps : null,
    sentence_count: sentenceCount,
    statement_count: statementCount,
    check_error: errors.length ? errors.join('; ') : null,
    chunked: chunks.length > 1,
    failed_chunks: failedIndices.length > 0 ? failedIndices.length : undefined,
  };
}

/**
 * Merge the session-scoped chunk loop (#81): statements/near-misses come from
 * the per-chunk `rules.extract` responses (chunk-local spans shifted by
 * baseOffset); conflicts/similarities/gaps come from the final accumulated
 * `rules.check`. IRIs are server-minted and globally unique — no `cN:`
 * prefixing, so the check's interactions reference statements verbatim.
 */
export function mergeSessionChunkedResults(
  chunkResults: (ExtractChunkResponse | null)[],
  chunks: TextChunk[],
  checkResult: CheckRulesResponse | null,
  failedIndices: number[] = []
): CheckRulesResponse {
  const statements: StatementDisplay[] = [];
  const nearMisses: NearMissDisplay[] = [];
  let sentenceCount = 0;
  let statementCount = 0;

  chunkResults.forEach((result, i) => {
    if (!result) return;
    const base = chunks[i]!.baseOffset;
    for (const s of result.statements || []) {
      statements.push({
        ...s,
        span_begin: s.span_begin != null ? s.span_begin + base : s.span_begin,
        span_end: s.span_end != null ? s.span_end + base : s.span_end,
      });
    }
    for (const nm of result.near_misses || []) {
      nearMisses.push({
        ...nm,
        span_begin:
          nm.span_begin != null ? nm.span_begin + base : nm.span_begin,
        span_end: nm.span_end != null ? nm.span_end + base : nm.span_end,
      });
    }
    sentenceCount += result.sentence_count || 0;
    statementCount += result.statement_count || 0;
  });

  // checkResult.statements is intentionally ignored — in session mode the
  // final check carries interactions only; statements come from the chunks.
  return {
    statements,
    conflicts: checkResult?.conflicts ?? null,
    similarities: checkResult?.similarities ?? null,
    gaps: checkResult?.gaps ?? null,
    near_misses: nearMisses.length ? nearMisses : null,
    sentence_count: sentenceCount,
    statement_count: statementCount,
    check_error: checkResult?.check_error ?? null,
    chunked: true,
    failed_chunks: failedIndices.length > 0 ? failedIndices.length : undefined,
  };
}
